import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  router,
  useFocusEffect,
  useLocalSearchParams,
  useNavigation,
  useRouter,
  type Href,
  type RoutePath,
  type RouteParams,
} from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { Main } from '@expo/html-elements';

import { getA11ySelector, useA11ySelector } from '@sg/a11y-helpers';
import {
  Alert,
  Container,
  FadeView,
  HStack,
  LoadingAnimation,
  slugify,
  theme,
  useResponsive,
  useScrollspy,
} from '@sg/garnish';
import { trackRadarMenuViewed } from '@sg/radar-fe';
import { MenuDietaryPreferences } from '@sg/shared-fe-features-menu';

import {
  MenuCategoriesGrid,
  MenuFloatingButtons,
  MenuOnlineOrderingUnavailable,
} from '@order/components-menu';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { telemetryLocationFromInput, useTelemetry, useTrackEventOnFocus } from '@order/Telemetry';

import { useBraze } from '../../../../context/Braze';
import { useSetLastInteractedMenuStore } from '../../../../context/LastInteractedStore';
import { useCart } from '../../../../hooks/useCart/useCart';
import { useCustomScreenTitle } from '../../../../hooks/useCustomScreenTitle';
import { useDeliveryOrderInFlight } from '../../../../hooks/useDeliveryOrderInFlight';
import { useMenuReorderRailOrders } from '../../../../hooks/useMenuReorderRailOrders';
import { useReorderIfRequired } from '../../../../hooks/useReorderIfRequired';
import { AppFooter } from '../../../AppFooter';
import { useDietaryPreferencesState } from '../../../DietaryRestrictions';
import { MenuReorderRail } from '../../../MenuReorderRail';
import {
  useMenuContentNavigation,
  type useMenuContent,
  type useProductDetailsContentData,
} from '../../hooks';
import {
  type MenuContentCategoryWithSlug,
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../MenuContent.types';
import {
  MenuContentDietaryPreferencesModal,
  useMenuContentDietaryPreferencesModal,
} from '../MenuContentDietaryPreferencesModal';
import { MenuContentHeader } from '../MenuContentHeader';
import { MenuContentNavigationHeader } from '../MenuContentNavigationHeader';
import { MenuContentProductCard } from '../MenuContentProductCard';
import { MenuContentTopDownModal } from '../MenuContentTopDownModal';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentReady = memo((props: MenuContentReadyProps) => {
  const navigation = useNavigation();
  const localParams = useLocalSearchParams<MenuRoute>();

  // ─── Derived ─────────────────────────────────────────────────────────

  const routes = navigation.getState()?.routes ?? [];
  const menuScreensCount = routes.filter((route) => MENU_SCREEN_NAMES.some((name) => name === route.name)).length; // prettier-ignore
  const hasMultipleMenuScreens = menuScreensCount > 1;

  // NOTE: Stash params in a ref so the layout effect picks up the current
  //       screen's params without re-firing on every render.
  const localParamsRef = useRef(localParams);
  localParamsRef.current = localParams;

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Ensures the customer only has a single active menu screen in the stack by
   * dismissing any others and re-anchoring to the current menu's href.
   */
  const dismissOtherMenuScreens = useCallback(() => {
    if (!hasMultipleMenuScreens) return;

    if (router.canDismiss()) {
      router.dismissAll();
    }

    const href = getMenuHref(localParamsRef.current);

    router.replace(href);
  }, [hasMultipleMenuScreens]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(dismissOtherMenuScreens, [dismissOtherMenuScreens]);

  // ─────────────────────────────────────────────────────────────────────

  // While the layout effect is dismissing + replacing, render loading state.
  if (hasMultipleMenuScreens) {
    return <LoadingAnimation />;
  }

  return <Content {...props} />;
});

// ─────────────────────────────────────────────────────────────────────────────

export const Content = (props: MenuContentReadyProps) => {
  const {
    restaurantData,
    deliveryDetailsData,
    orderChannel,
    locationName,
    productDetailsContentData,
    categoriesBadges,
    caloriesOverrides,
    reorderRailOrders,
  } = props;

  // ─────────────────────────────────────────────────────────────────────

  const { minWidth, match, currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();
  const { lineItemsCount } = useCart();
  const { trackEvent } = useTelemetry();
  const { logCustomEvent } = useBraze();

  const locationHeaderA11ySelector = useA11ySelector('menu.location-header');

  const routeParams = useLocalSearchParams<{ referrer?: string; category?: string }>();

  const { referrer } = routeParams ?? {};

  // NOTE: Scrollspy updates the `category` route param after mount, so we
  // capture the initial value to detect whether the user deep-linked into a
  // category via URL anchor (e.g. `/menu/abc?category=wraps`).
  const hadInitialCategoryAnchorRef = useRef(Boolean(routeParams.category));

  // ─────────────────────────────────────────────────────────────────────

  const {
    menu,
    hours,
    id: restaurantId,
    name: restaurantName,
    slug: restaurantSlug,
    isAcceptingOrders = true,
    notAcceptingOrdersReason,
  } = restaurantData;

  // NOTE: We need to hide empty categories and enrich with slugs for scroll spy anchors
  const filteredCategories = useMemo<MenuContentCategoryWithSlug[]>(
    () =>
      menu.categories
        .filter((category) => category.products.length > 0)
        .map((category) => ({ ...category, slug: slugify(category.name) })),
    [menu.categories],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isTopDownCategoriesModalEnabled = useFeatureFlag(
    'CELS-3094-menu-top-down-categories-enabled',
  );
  const isMacronutrientsEnabled = useFeatureFlag('CELS-4323-order-macronutrients-enabled');
  const isProductDetailsPageEnabled = useFeatureFlag(
    'CELS-4497-order-product-details-page-enabled',
  );
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');
  const isOutpost = orderChannel === 'outpost';

  const shouldRenderReorderRailSection = reorderRailOrders.mostRecentLineItems.length > 0;
  const shouldShowBagFloatingButton =
    currentBreakpoint.isXS && lineItemsCount > 0 && !isNavbarV2Enabled;
  const shouldRenderHoursMessage = isOutpost && typeof hours.formatted === 'string';

  const shouldRenderTopDownCategoriesModal =
    referrer !== 'bag' &&
    referrer !== 'reorder-confirm-location' &&
    isTopDownCategoriesModalEnabled &&
    currentBreakpoint.isXS &&
    !hadInitialCategoryAnchorRef.current;

  // ─── State ───────────────────────────────────────────────────────────

  const {
    hasOpenedDietaryPreferences,
    setHasOpenedDietaryPreferencesState,
    isResolvingDietaryPreferencesState,
  } = useDietaryPreferencesState();

  const {
    isDietaryPreferencesModalVisible,
    showDietaryPreferencesModal,
    hideDietaryPreferencesModal,
  } = useMenuContentDietaryPreferencesModal();

  const [isShowingMenuCategoriesModal, setIsShowingMenuCategoriesModal] = useState(
    shouldRenderTopDownCategoriesModal,
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const { generateCustomizationHref, setTargetParam, defaultActiveTargetId, changeLocation } =
    useMenuContentNavigation({
      orderChannel,
      restaurantSlug,
      deliveryDetailsData,
    });

  const onContactUs = useCallback(() => {
    trackEvent({ name: 'menu.contact_us' });
  }, [trackEvent]);

  const hideMenuCategoriesModal = useCallback(() => {
    setIsShowingMenuCategoriesModal(false);
  }, []);

  const showTopDownModal = useCallback(() => {
    setIsShowingMenuCategoriesModal(true);
  }, []);

  const {
    activeTargetId,
    scrollViewRef,
    navScrollViewRef,
    scrollOffset,
    scrollTo,
    measureCoordinates,
    deregisterTarget,
    onScrollViewContentSizeChange,
    onScrollViewLayout,
  } = useScrollspy({
    defaultTargetId:
      defaultActiveTargetId ??
      (shouldRenderReorderRailSection ? REORDER_RAIL_TARGET_ID : filteredCategories?.[0]?.slug),
    onActiveTargetIdChange: setTargetParam,
  });

  const onNavItemPress = useCallback(
    (targetId: string) => {
      if (targetId === REORDER_RAIL_TARGET_ID) {
        scrollTo(targetId);

        trackEvent({
          name: 'menu.jump_nav',
          payload: { id: restaurantSlug, tabName: REORDER_RAIL_TARGET_ID },
        });

        return;
      }

      const targetCategory = filteredCategories.find((category) => category.slug === targetId);

      if (!targetCategory) return;

      scrollTo(targetId);

      trackEvent({
        name: 'menu.jump_nav',
        payload: { id: restaurantSlug, tabName: targetCategory.name },
      });
    },
    [filteredCategories, restaurantSlug, scrollTo, trackEvent],
  );

  const onDietaryPreferencesModalClose = useCallback(() => {
    hideDietaryPreferencesModal();
    void setHasOpenedDietaryPreferencesState();
  }, [hideDietaryPreferencesModal, setHasOpenedDietaryPreferencesState]);

  // NOTE: In case of `undefined`, the default `goBack` action will be used.
  const customBackButtonHandler =
    shouldRenderTopDownCategoriesModal && !isShowingMenuCategoriesModal
      ? showTopDownModal
      : undefined;

  const router = useRouter();

  const openBag = useCallback(() => {
    router.navigate('/bag');
  }, [router]);

  const trackMenuViewBrazeEvent = useCallback(() => {
    logCustomEvent('menu.view');
  }, [logCustomEvent]);

  const trackMenuViewRadarEvent = useCallback(() => {
    trackRadarMenuViewed(restaurantSlug);
  }, [restaurantSlug]);

  useDeliveryOrderInFlight();
  useReorderIfRequired(restaurantSlug, deliveryDetailsData?.addressId);

  useSetLastInteractedMenuStore({
    restaurantId,
    restaurantName,
    restaurantSlug,
    addressId: deliveryDetailsData?.addressId,
    deliveryFee: deliveryDetailsData?.deliveryFee,
    vendor: deliveryDetailsData?.vendor,
    vendorRestaurantId: deliveryDetailsData?.vendorRestaurantId,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus({
    name: 'menu.view',
    payload: { restaurant: telemetryLocationFromInput(restaurantData) },
  });

  useCustomScreenTitle(
    formatMessage(messages.orderChannelAndLocationText, {
      order_channel: orderChannel,
      location: locationName,
    }),
  );

  useFocusEffect(trackMenuViewBrazeEvent);
  useFocusEffect(trackMenuViewRadarEvent);

  // ─── Styles ──────────────────────────────────────────────────────────

  const infoMessagesContainerStyles = [
    styles.infoMessagesContainer,
    match([styles.infoMessagesContainerXS, styles.infoMessagesContainerSM]),
  ];
  const reorderRailInfoMessagesContainerStyles = [
    styles.infoMessagesContainer,
    match([styles.reorderRailInfoMessagesContainerXS, styles.reorderRailInfoMessagesContainerSM]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  const shouldRenderDietaryPreferencesToggle =
    !isResolvingDietaryPreferencesState && !hasOpenedDietaryPreferences;

  const shouldRenderFirstPositionInfoMessages =
    shouldRenderHoursMessage || shouldRenderDietaryPreferencesToggle;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <View style={styles.container}>
        {/* ─── Navigation Header ────────────────────────────────────────── */}

        <MenuContentNavigationHeader
          orderChannel={orderChannel}
          locationName={locationName}
          onBackBtnPressOverride={customBackButtonHandler}
        />

        {/* ─── Header ───────────────────────────────────────────────────── */}

        <MenuContentHeader
          menuGridScrollOffsetSV={scrollOffset}
          restaurantData={restaurantData}
          locationName={locationName}
          orderChannel={orderChannel}
          deliveryDetailsData={deliveryDetailsData}
          onContactUs={onContactUs}
          {...locationHeaderA11ySelector.props}
        >
          <MenuCategoriesGrid.Nav ref={navScrollViewRef} testID="menu-content.categories-nav">
            {shouldRenderReorderRailSection ? (
              <MenuCategoriesGrid.NavItem
                category={formatMessage(messages.reorderRailNavLabel)}
                targetId={REORDER_RAIL_TARGET_ID}
                navScrollViewRef={navScrollViewRef}
                onPress={onNavItemPress}
                isActive={activeTargetId === REORDER_RAIL_TARGET_ID}
              />
            ) : null}

            {filteredCategories.map((category) => (
              <MenuCategoriesGrid.NavItem
                key={category.id}
                category={category.name}
                targetId={category.slug}
                navScrollViewRef={navScrollViewRef}
                onPress={onNavItemPress}
                isActive={activeTargetId === category.slug}
              />
            ))}
          </MenuCategoriesGrid.Nav>
        </MenuContentHeader>

        {/* ─── Categories ───────────────────────────────────────────────── */}

        <MenuCategoriesGrid.Container
          // @ts-expect-error - AnimatedRef is not assignable to Ref (reanimated v4)
          ref={scrollViewRef}
          testID="menu-content.categories-container"
          onContentSizeChange={onScrollViewContentSizeChange}
          onLayout={onScrollViewLayout}
        >
          <Main style={styles.contentContainer}>
            {shouldRenderReorderRailSection ? (
              <MenuCategoriesGrid.Category
                style={styles.reorderRailSection}
                targetId={REORDER_RAIL_TARGET_ID}
                measureCoordinates={measureCoordinates}
                deregisterTarget={deregisterTarget}
              >
                {shouldRenderFirstPositionInfoMessages ? (
                  <Container>
                    <View style={reorderRailInfoMessagesContainerStyles}>
                      {shouldRenderHoursMessage ? (
                        <Alert.Container palette="info">
                          <Alert.Text>{hours.formatted}</Alert.Text>
                        </Alert.Container>
                      ) : null}

                      {shouldRenderDietaryPreferencesToggle ? (
                        <MenuDietaryPreferences.Toggle onPress={showDietaryPreferencesModal} />
                      ) : null}
                    </View>
                  </Container>
                ) : null}

                <MenuReorderRail
                  lineItems={reorderRailOrders.mostRecentLineItems}
                  mostRecentOrder={reorderRailOrders.mostRecentOrder}
                  restaurantId={restaurantId}
                  restaurantName={restaurantName}
                  restaurantSlug={restaurantSlug}
                  deliveryDetails={deliveryDetailsData}
                />
              </MenuCategoriesGrid.Category>
            ) : null}

            <Container>
              {filteredCategories.map((category, index) => {
                const isFirstCategory = index === 0;
                const isCustomCategory = category.isCustom;

                const shouldRenderStoreHoursMessage = !shouldRenderReorderRailSection && isFirstCategory && shouldRenderHoursMessage; // prettier-ignore
                const shouldRenderCategoryDietaryPreferencesToggle = !shouldRenderReorderRailSection && isFirstCategory && shouldRenderDietaryPreferencesToggle; // prettier-ignore
                const shouldRenderInfoMessages = shouldRenderStoreHoursMessage || shouldRenderCategoryDietaryPreferencesToggle; // prettier-ignore

                const a11ySelector = getA11ySelector('menu.categories-grid.category.container', {
                  group: category.name.toLowerCase(),
                  index,
                });

                const maybeCategoryBadge = categoriesBadges[category.id];

                // ─── Custom Category ───────────────────────────────────────────

                if (isCustomCategory) {
                  const customProducts = category.products.filter((product) => product.isCustom);

                  if (customProducts.length === 0) return null;

                  const hasMultipleProducts = customProducts.length > 1;
                  const itemsPerRow = hasMultipleProducts ? match([1, 1, 2]) : 1;

                  return (
                    <MenuCategoriesGrid.Category
                      key={category.id}
                      targetId={category.slug}
                      // @ts-expect-error - scrollViewRef type mismatch
                      scrollViewRef={scrollViewRef}
                      measureCoordinates={measureCoordinates}
                      deregisterTarget={deregisterTarget}
                      {...a11ySelector.props}
                    >
                      <HStack itemsPerRow={itemsPerRow} gap={theme.spacing['4']}>
                        {customProducts.map((product) => (
                          <MenuCategoriesGrid.CustomProductCard
                            key={product.id}
                            product={product}
                            generateHref={generateCustomizationHref}
                          />
                        ))}
                      </HStack>
                    </MenuCategoriesGrid.Category>
                  );
                }

                // ─── Standard Category ─────────────────────────────────────────

                return (
                  <MenuCategoriesGrid.Category
                    key={category.id}
                    targetId={category.slug}
                    measureCoordinates={measureCoordinates}
                    deregisterTarget={deregisterTarget}
                    {...a11ySelector.props}
                  >
                    {/* NOTE: We use the first category to render extra components */}

                    {shouldRenderInfoMessages ? (
                      <View style={infoMessagesContainerStyles}>
                        {shouldRenderStoreHoursMessage ? (
                          <Alert.Container palette="info">
                            <Alert.Text>{hours.formatted}</Alert.Text>
                          </Alert.Container>
                        ) : null}

                        {shouldRenderCategoryDietaryPreferencesToggle ? (
                          <MenuDietaryPreferences.Toggle onPress={showDietaryPreferencesModal} />
                        ) : null}
                      </View>
                    ) : null}

                    <MenuCategoriesGrid.CategoryHeading badge={maybeCategoryBadge}>
                      {category.name}
                    </MenuCategoriesGrid.CategoryHeading>

                    <MenuCategoriesGrid.ProductsStack>
                      {category.products.map((product) => {
                        const { id } = product;

                        const productDetailsContentDataItem = productDetailsContentData?.get(product.baseProduct.slug); // prettier-ignore
                        const productDetailsData = productDetailsContentDataItem?.fields; // prettier-ignore
                        const hasProductDetails = Boolean(productDetailsData?.isEnabled); // prettier-ignore

                        const cardHoverImage = productDetailsData?.cardHoverImage; // prettier-ignore
                        const cardHoverStaticImage = productDetailsData?.cardHoverStaticImage; // prettier-ignore
                        const carousel = productDetailsData?.carousel;

                        const hoverImageUrl = cardHoverImage?.fields?.file?.url;
                        const hoverStaticImageUrl = cardHoverStaticImage?.fields?.file?.url;
                        const hoverImageAlt = cardHoverImage?.fields?.title;
                        const caloriesOverrideEntry = caloriesOverrides?.[product.baseProduct.slug];

                        return (
                          <MenuContentProductCard
                            key={id}
                            product={product}
                            hasProductDetails={hasProductDetails}
                            restaurantSlug={restaurantSlug}
                            deliveryDetails={deliveryDetailsData}
                            generateHref={generateCustomizationHref}
                            imageFetchingPriority={isShowingMenuCategoriesModal ? 'low' : 'normal'} // prettier-ignore
                            shouldShowNutritionData={isMacronutrientsEnabled}
                            shouldShowNutritionLink={isProductDetailsPageEnabled} // prettier-ignore
                            hoverImageUrl={hoverImageUrl}
                            hoverStaticImageUrl={hoverStaticImageUrl}
                            hoverImageAlt={hoverImageAlt}
                            carousel={carousel}
                            calorieOverride={caloriesOverrideEntry?.caloriesOverride}
                            calorieOverrideText={caloriesOverrideEntry?.caloriesOverrideText}
                          />
                        );
                      })}
                    </MenuCategoriesGrid.ProductsStack>
                  </MenuCategoriesGrid.Category>
                );
              })}
            </Container>
          </Main>

          {minWidth.isSM ? <AppFooter /> : null}
        </MenuCategoriesGrid.Container>
      </View>

      {/* ─── Modals ─────────────────────────────────────────────────────── */}

      <MenuContentDietaryPreferencesModal
        isVisible={isDietaryPreferencesModalVisible}
        onClose={onDietaryPreferencesModalClose}
      />

      {isAcceptingOrders ? null : (
        <MenuOnlineOrderingUnavailable.Container>
          <MenuOnlineOrderingUnavailable.Dialog>
            <MenuOnlineOrderingUnavailable.Message message={notAcceptingOrdersReason} />
            <MenuOnlineOrderingUnavailable.Button onPress={changeLocation} />
          </MenuOnlineOrderingUnavailable.Dialog>
        </MenuOnlineOrderingUnavailable.Container>
      )}

      {shouldRenderTopDownCategoriesModal ? (
        <MenuContentTopDownModal
          orderChannel={orderChannel}
          locationName={locationName}
          categories={filteredCategories}
          categoriesBadges={categoriesBadges}
          isVisible={isShowingMenuCategoriesModal}
          hideModal={hideMenuCategoriesModal}
          onCategoryPress={scrollTo}
          recentOrdersTargetId={shouldRenderReorderRailSection ? REORDER_RAIL_TARGET_ID : undefined}
        />
      ) : null}

      {/* ─── Floating Buttons ───────────────────────────────────────────── */}

      <FadeView show={shouldShowBagFloatingButton}>
        <MenuFloatingButtons.Bag lineItemsCount={lineItemsCount} onPress={openBag} />
      </FadeView>
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getMenuHref(forwardedParams: MenuLocalParams): Href {
  if (checkIfDeliveryMenuParams(forwardedParams)) {
    return { pathname: MENU_PATHNAMES.delivery, params: forwardedParams };
  }

  return { pathname: MENU_PATHNAMES.standard, params: forwardedParams };
}

function checkIfDeliveryMenuParams(
  params: MenuLocalParams,
): params is Extract<MenuLocalParams, { addressId: string }> {
  return 'addressId' in params;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const REORDER_RAIL_TARGET_ID = 'order-again';

const MENU_PATHNAMES = {
  standard: '/[restaurantSlug]/menu',
  delivery: '/delivery/[addressId]/menu',
} as const satisfies Record<string, RoutePath>;

const MENU_SCREEN_NAMES = [
  '[restaurantSlug]/menu/index',
  'delivery/[addressId]/menu/index',
] as const satisfies readonly StackRouteName<MenuRoute>[];

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryEtaText: {
    defaultMessage: 'Delivers in {eta} minutes',
    description: 'Menu | Header | Delivery ETA text',
  },
  orderChannelAndLocationText: {
    defaultMessage: `{order_channel, select,
      pickup {Pickup from {location}}
      delivery {Delivery to {location}}
      outpost {Outpost at {location}}
      other {}
    }`,
    description: 'Menu | Navigation Header | Order channel and location text',
  },
  reorderRailNavLabel: {
    defaultMessage: 'Order again',
    description: 'Menu | Categories Nav | Reorder rail nav item label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: theme.spacing['16'],

    // required for web `<Main>` component

    display: 'flex',
    flexDirection: 'column',
  },

  infoMessagesContainer: {
    alignItems: 'flex-start',
    gap: theme.spacing['4'],
    marginBottom: theme.spacing['8'],
  },
  infoMessagesContainerXS: {
    marginTop: -theme.spacing['4'],
  },
  infoMessagesContainerSM: {
    marginTop: -theme.spacing['8'],
  },
  reorderRailInfoMessagesContainerXS: {
    marginTop: theme.spacing['2'],
  },
  reorderRailInfoMessagesContainerSM: {
    marginTop: theme.spacing['6'],
  },
  reorderRailSection: {
    paddingTop: 0,
    paddingBottom: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentReadyProps = {
  restaurantData: MenuContentRestaurantData;
  productDetailsContentData: ProductDetailsContentData;
  categoriesBadges: CategoriesBadges;
  caloriesOverrides: CaloriesOverrides;
  deliveryDetailsData?: MenuContentDeliveryDetailsData;
  orderChannel: MenuContentOrderChannel;
  locationName: string;
  reorderRailOrders: ReturnType<typeof useMenuReorderRailOrders>;
};

type MenuRoute = (typeof MENU_PATHNAMES)[keyof typeof MENU_PATHNAMES];
type MenuLocalParams = RouteParams<MenuRoute>;

type ProductDetailsContentData = ReturnType<
  typeof useProductDetailsContentData
>['productDetailsContentData'];

type CategoriesBadges = ReturnType<typeof useMenuContent>['mappedContent']['categoriesBadges'];
type CaloriesOverrides = ReturnType<typeof useMenuContent>['mappedContent']['caloriesOverrides'];

type StackRouteName<R extends RoutePath> = R extends `/${infer Path}` ? `${Path}/index` : never;
